import { Component, OnInit } from '@angular/core'
import { FormGroup, FormBuilder, Validators } from '@angular/forms'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { CategorySelection, EmployeeAPIModel, QueriesAPIModel, TaskTypeAPIModel } from 'src/app/models/api.model'
import { CommonService, addDaysToEditableDatePickerFormat, convertToISO } from 'src/app/services/common/common.service'
import { InitializeService } from 'src/app/services/initialize/initialize.service'
import { Observable, startWith, map, of, Subscription, Subject, catchError } from 'rxjs'
import { QueryService } from 'src/app/services/query/query.service'
import {
  DISPLAY_ID,
  DOCUMENT_NAMES,
  ENTITY_ID,
  CATEGORY,
  QUERY_NAME,
  DESCRIPTION,
  EMPLOYEE_ID,
  RESPONSE_DUE_BY,
  QUERY_DISPLAY_ID,
  CATEGORY_OF_QUERY,
  TAG,
  NOTES,
  DEPT_TIMELINE,
  NEXT_INTERNAL_DUE_DATE,
  PRIORITY_LEVEL,
  ASSIGN_TO,
  STATUS
} from 'src/app/constants/excel.columns'
import { AbstractValidator, IColumn } from '../../import/validators/abstract-validator.interface'

@Component({
  selector: 'app-create-query',
  templateUrl: './create-query.component.html',
  styleUrls: ['./create-query.component.scss']
})
export class CreateQueryComponent implements OnInit {
  createQueryForm: FormGroup
  filesToBeUploaded: File[]

  queryTaskTypes: TaskTypeAPIModel[] = []
  uploadedDocuments: any[]

  readonly titleControlName = 'title'
  readonly OWNER_ID_CONTROL_NAME = 'ownerId'
  readonly RESPONSE_DUE_DATE_TIME_CONTROL_NAME = 'responseDueDatetime'

  selectTaskTypesSubscription$: any

  filteredTaskTypes: Observable<TaskTypeAPIModel[]>

  user$: Subscription

  readonly userType = GlobalConstants.USER_TYPE
  otherQueryTask: TaskTypeAPIModel

  private readonly OTHER_QUERY = 'OTHER_QUERY'
  private readonly TASK_CATEGORY = 'taskCategory'
  private readonly TASK_SUB_CATEGORY = 'taskSubCategory'
  readonly module = GlobalConstants.MODULE.QUERIES
  showImport = false
  showUpdateImport = false
  importValidator: QueryValidator
  updateImportValidator: UpdateImportQueryValidator
  selectedEmployeeCompanyEntityId: string | undefined

  constructor(
    private formBuilder: FormBuilder,
    private queryService: QueryService,
    private cs: CommonService,
    private initializeService: InitializeService
  ) {
    this.importValidator = new QueryValidator(queryService)
    this.updateImportValidator = new UpdateImportQueryValidator(queryService)
  }

  ngOnInit(): void {
    this.buildCreateQueryForm()
    this.filteredTaskTypes = this.createQueryForm.get(this.titleControlName).valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    )

    this.createQueryForm?.get(this.titleControlName)?.valueChanges?.subscribe(value => {
      const selectedTaskType = this.queryTaskTypes.find(taskType => taskType.code === value) || this.otherQueryTask
      const responseDueInDays = selectedTaskType?.responseDueInDays

      if (responseDueInDays && this.cs.isNumber(responseDueInDays)) {
        const date = addDaysToEditableDatePickerFormat(Number(responseDueInDays))
        this.createQueryForm.get(this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME).setValue(date)
      }
    })
  }

  buildCreateQueryForm() {
    this.createQueryForm = this.formBuilder.group({
      [this.titleControlName]: ['', [Validators.required]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5000)]],
      responseDueInDays: [null, []],
      [this.TASK_CATEGORY]: ['', [Validators.required]],
      [this.TASK_SUB_CATEGORY]: ['', [Validators.required]],
      [this.RESPONSE_DUE_DATE_TIME_CONTROL_NAME]: ['', [Validators.required]],
      [this.OWNER_ID_CONTROL_NAME]: ['', [Validators.required]]
    })
  }

  saveAndExit() {
    if (this.createQueryForm.invalid) {
      this.createQueryForm.markAllAsTouched()
      return
    }

    const selectedTaskType =
      this.queryTaskTypes.find(taskType => taskType.label === this.createQueryForm.value[this.titleControlName]) ||
      this.otherQueryTask

    const statusCode = selectedTaskType?.statusList.find(status => status.defaultStatus)?.code
    const nextAction = selectedTaskType?.statusList.find(status => status.defaultStatus)?.nextActionToBeTaken

    const responseDueInDays = selectedTaskType?.responseDueInDays

    if (!responseDueInDays) {
      return this.cs.showError('Kindly select Response Due By')
    }

    if (!statusCode) {
      return this.cs.showError('Query Status is required')
    }

    if (!selectedTaskType?.code) {
      return this.cs.showError('Query Code is required')
    }

    const query = {
      ...this.createQueryForm.value,
      documents: this.uploadedDocuments,
      responseDueInDays,
      status: statusCode,
      taskCode: selectedTaskType.code,
      companyEntityId: this.selectedEmployeeCompanyEntityId,
      nextAction: nextAction
    }

    this.dispatchSaveQueryAction(query, this.filesToBeUploaded)
  }

  private dispatchSaveQueryAction(query: QueriesAPIModel, files: File[]) {
    this.queryService.dispatchSaveQueryAction(
      query,
      files || [],
      this.cs.createRoutingURL([GlobalConstants.ROUTING.QUERY, GlobalConstants.ROUTING.DASHBOARD])
    )
  }

  exit() {
    this.cs.route(GlobalConstants.ROUTING.QUERY, GlobalConstants.ROUTING.DASHBOARD)
  }

  handleFileInput(files: File[]) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  get f() {
    return this.createQueryForm.controls
  }

  private getQueryTaskTypes(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection

    this.otherQueryTask = null
    this.queryTaskTypes = []
    this.filteredTaskTypes = null
    this.selectTaskTypesSubscription$ = this.initializeService
      .getFilteredTaskTypes(GlobalConstants.MODULE.QUERIES, selectedCategory, selectedSubCategory)
      .subscribe((taskTypes: TaskTypeAPIModel[]) => {
        if (taskTypes) {
          this.queryTaskTypes = [...taskTypes]
          this.otherQueryTask = this.queryTaskTypes.filter(taskType => taskType.code.startsWith(this.OTHER_QUERY))?.[0]
          const taskTypesToShow = this.queryTaskTypes.filter(taskType => !taskType.code.startsWith(this.OTHER_QUERY))
          this.filteredTaskTypes = of(taskTypesToShow)
        }
      })
  }

  onCategorySelected(categorySelection: CategorySelection) {
    const { selectedCategory, selectedSubCategory } = categorySelection
    this.createQueryForm.get(this.titleControlName).markAsUntouched()
    this.createQueryForm.get(this.titleControlName).setValue('')
    this.createQueryForm.get(this.TASK_CATEGORY).setValue(selectedCategory)
    this.createQueryForm.get(this.TASK_SUB_CATEGORY).setValue(selectedSubCategory)
    this.queryTaskTypes = []
    this.getQueryTaskTypes(categorySelection)
  }

  private _filter(value: string): TaskTypeAPIModel[] {
    const filterValue = this._normalizeValue(value)
    return this.queryTaskTypes.filter(queryTaskType => this._normalizeValue(queryTaskType.label).includes(filterValue))
  }

  private _normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '')
  }

  handleEmployeeSelectedValue(ownerId: string) {
    this.createQueryForm.patchValue({ ownerId })
  }

  handleEmployeeSelected(employee: EmployeeAPIModel) {
    this.selectedEmployeeCompanyEntityId = employee?.companyEntityId
  }

  ngOnDestroy() {
    this.selectTaskTypesSubscription$?.unsubscribe()
  }
}

export class QueryValidator extends AbstractValidator<QueriesAPIModel> {
  public override allRows: Subject<QueriesAPIModel[]> = new Subject<QueriesAPIModel[]>()
  public override title = 'Query'
  public override templateName: string = 'Query_Template.xlsx'
  public override templateURL: string = ''
  public override saveResponse: Subject<any> = new Subject<any>()
  public override _header: Map<string, IColumn> = new Map([
    ['Entity ID', ENTITY_ID],
    ['Employee ID', EMPLOYEE_ID],
    ['Display ID', DISPLAY_ID],
    ['Name', QUERY_NAME],
    ['Description', DESCRIPTION],
    ['Response Due By', RESPONSE_DUE_BY],
    ['Category', CATEGORY],
    ['Document Names', { ...DOCUMENT_NAMES, validations: [] }]
  ])

  public override document: { isRequired: boolean; showUpload: boolean; file$: Subject<File> } = {
    isRequired: false,
    showUpload: true,
    file$: new Subject<File>()
  }

  public override startImporting$: Subject<boolean> = new Subject<boolean>()
  public override apiResponse$: Subject<any> = new Subject<boolean>()
  private rowsToSave = []
  private documentToSave = null
  constructor(private queryService: QueryService, u: QueriesAPIModel = null) {
    super(u)

    this.allRows.subscribe(rowsToSave => {
      this.rowsToSave = rowsToSave
    })

    this.document.file$.subscribe((file: File) => {
      this.documentToSave = file
    })

    this.subscribeImportStart()
  }

  private subscribeImportStart() {
    this.startImporting$.subscribe(doStartImporting => {
      if (doStartImporting) {
        const queries = this.createQueriesList()
        this.queryService
          .invokeSaveBulkQueryAPI(queries, this.documentToSave)
          .pipe(
            map((response: any) => {
              this.apiResponse$.next({ status: 1, response: { ...response, fileName: 'queries.xlsx' } })
            }),
            catchError((error: any) => {
              this.apiResponse$.next({ status: 0, error })
              return of(null)
            })
          )
          .subscribe()
      }
    })
  }

  private createQueriesList(): QueriesAPIModel[] {
    const queries = []
    for (const row of this.rowsToSave) {
      const query = {}
      for (const key of Object.keys(row)) {
        if (key) {
          if (key === DOCUMENT_NAMES.key && row[key]) {
            const documents = []
            if (row[key].includes(',')) {
              const documentSplits = row[key].split(',')
              for (const documentSplit of documentSplits) {
                documents.push(documentSplit.trim())
              }
            } else {
              documents.push(row[key].trim())
            }
            query[key] = documents
            continue
          }
          if (!row[key] && key === DOCUMENT_NAMES.key) {
            query[key] = []
          } else {
            query[key] = row[key]
          }
        }
      }
      queries.push(query)
    }
    return queries
  }
}

export class UpdateImportQueryValidator extends AbstractValidator<QueriesAPIModel> {
  public override allRows: Subject<QueriesAPIModel[]> = new Subject<QueriesAPIModel[]>()
  public override title = 'Query'
  public override templateName: string = 'Query_Update_Template.xlsx'
  public override templateURL: string = ''
  public override saveResponse: Subject<any> = new Subject<any>()
  public override _header: Map<string, IColumn> = new Map([
    ['Entity ID', ENTITY_ID],
    ['Query ID', QUERY_DISPLAY_ID],
    ['Status', STATUS],
    ['Category of Query', CATEGORY_OF_QUERY],
    ['Priority Level', PRIORITY_LEVEL],
    ['Next Internal Due Date', NEXT_INTERNAL_DUE_DATE],
    ['Dept Timeline', DEPT_TIMELINE],
    ['Assign To', ASSIGN_TO],
    ['Tag', TAG],
    ['Notes', NOTES]
  ])

  public override document: { isRequired: boolean; showUpload: boolean; file$: Subject<File> } = {
    isRequired: false,
    showUpload: false,
    file$: new Subject<File>()
  }

  public override startImporting$: Subject<boolean> = new Subject<boolean>()
  public override apiResponse$: Subject<any> = new Subject<boolean>()
  private rowsToSave = []
  constructor(private readonly queryService: QueryService, u: QueriesAPIModel = null) {
    super(u)

    this.allRows.subscribe(rowsToSave => {
      this.rowsToSave = rowsToSave
    })

    this.subscribeImportStart()
  }

  private subscribeImportStart() {
    this.startImporting$.subscribe(doStartImporting => {
      if (doStartImporting) {
        const queries = this.createQueriesList()
        this.queryService
          .invokeUpdateBulkQueryAPI(queries)
          .pipe(
            map((response: any) => {
              this.apiResponse$.next({ status: 1, response: { ...response, fileName: 'queries.xlsx' } })
            }),
            catchError((error: any) => {
              this.apiResponse$.next({ status: 0, error })
              return of(null)
            })
          )
          .subscribe()
      }
    })
  }

  private createQueriesList(): QueriesAPIModel[] {
    const queries = []
    for (const row of this.rowsToSave) {
      const query = {}
      for (const key of Object.keys(row)) {
        if (key) {
          if ((key === NEXT_INTERNAL_DUE_DATE.key || key === DEPT_TIMELINE.key) && row[key]) {
            row[key] = convertToISO(row[key])
          }
        }
        query[key] = row[key]
      }
      queries.push(query)
    }
    return queries
  }
}
